import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NgbActiveModal, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { TextChar } from 'custom_modules/textcontrol-promises-develop/src/TextChar';
import { set } from 'lodash';
import { combineLatest, Subscription } from 'rxjs';
import { SearchScope, TXdocsSearchScope } from 'src/app/models/find-and-replace.model';
import { DocumentDataService } from 'src/app/services/document-data.service';
import { HelperService } from 'src/app/services/helper.service';
import { SpotlightService } from 'src/app/services/spotlight.service';
import { TextControlService } from 'src/app/services/text-control.service';
import { FindOptions, FindResult, FormField, Rectangle } from 'src/app/services/text-control.service.interface';
import { ToastService } from 'src/app/services/toast.service';
import { TextControlContext, FormField as ffPromise } from 'textcontrol-promises-develop';

declare const TXTextControl: any;

@Component({
  selector: 'tx-find-and-replace-dialog',
  templateUrl: './find-and-replace-dialog.component.html',
  styleUrls: ['./find-and-replace-dialog.component.scss'],
})
export class FindAndReplaceDialogComponent implements AfterViewInit, OnDestroy {
  @ViewChild('nextButton') nextButtonElement!: ElementRef;
  activeTab = 'find';
  searchUp: string = 'down';
  searchQuery: string = '';
  replaceQuery: string = '';
  findOptionsBitMask: number = 0;
  lastSearchResultBodyLocation: number = 0; //Cursor Position defined as -1 in TXTextControl
  lastSearchResultHeaderFooterLocation: number = 0;
  searchScope: string = SearchScope.Body; // Default to searching in the main text
  searchSelectionInvalid = false;
  txTextSelectionLength = 0;
  txDocsSearchScope: TXdocsSearchScope = {
    answerFields: false,
    calculatedFields: false,
    content: true,
  };
  public searchItems = [
    { Area: 'Content', Display: 'Text', Value: 'Body' },
    { Area: 'Answers', Display: 'Answers', Value: 'Answers' },
    { Area: 'Answers', Display: 'Calculated Answers', Value: 'CalculatedAnswers' },
  ];
  public positionEventsSubscription!: Subscription;
  zoom: number = 1.0;
  txtViewLoc = { x: 0, y: 0 };
  mainCanvas: HTMLElement | null | undefined;
  fieldNavigatorSideBar: HTMLElement | null | undefined;
  modal: HTMLElement | null | undefined;
  public keyDownHandler = this.onKeyDown.bind(this);

  constructor(
    public activeModal: NgbActiveModal,
    public documentDataService: DocumentDataService,
    public textControlService: TextControlService,
    public toastService: ToastService,
    public changeDetectorRef: ChangeDetectorRef,
    public helperService: HelperService,
    public spotlightService: SpotlightService
  ) {}
  ngOnDestroy(): void {
    if (this.positionEventsSubscription) {
      this.positionEventsSubscription.unsubscribe();
    }
    const container = document.getElementById('circleContainer');
    if (!container) {
      console.error('Container element not found.');
      return;
    }
    container.style.display = 'none';
    document.removeEventListener('keydown', this.keyDownHandler)
  }
  async ngAfterViewInit(): Promise<void> {
    let rect = { location: { x: 0, y: 0 }, size: { width: 0, height: 0 } };
    this.spotlightService.placeSpotlight(rect);
    console.log('targetRect', rect);
    const container = document.getElementById('circleContainer');
    if (!container) {
      console.error('Container element not found.');
      return;
    }
    container.style.display = 'block';
    let txContext = new TextControlContext();
    if ((await txContext.selection.getLength()) != 0) {
      this.searchQuery = await txContext.selection.getText();
      this.replaceQuery = '';
      txContext.selection.setLength(0);
    }
    try {
      const ff = await this.textControlService.getActiveField();
      if (ff) {
        const formField = new ffPromise(ff);
        const start = await formField.getStart();
        txContext.setInputPositionByTextPosition(start - 1, 2);
      }
    } catch (error) {
      console.error('Error getting active field', error);
    }

    this.positionEventsSubscription = combineLatest([
      this.textControlService.watchDocumentAndSelectionChangeObservable(),
      this.textControlService.position$,
    ]).subscribe((event) => {
      this.zoom = event[1].zoom / 100;
      this.txtViewLoc = event[1].location;
      this.txTextSelectionLength = event[0].selection.length;
      if (event[0].selection.length == 0) {
        return;
      }

      Promise.all([
        txContext.textChars.elementAt(event[0].selection.start),
        txContext.textChars.elementAt(
          event[0].selection.start + (event[0].selection.length == 0 ? 1 : event[0].selection.length)
        ),
      ]).then((locationValues) => {
        let textCharStart = new TextChar(locationValues[0]);
        let textCharEnd = new TextChar(locationValues[1]);
        Promise.all([textCharStart.getBounds(), textCharEnd.getBounds()]).then((bounds) => {
          let merged = this.mergeRectangles(bounds[0], bounds[1]);
          let mainCanvas = document.getElementById('mainCanvas');
          if (!mainCanvas) {
            console.error('Canvas element not found.');
            return;
          }
          const rect = mainCanvas.getBoundingClientRect();
          // not taking into account scroll offset and assuming 100% zoom
          merged.location.x = rect.left + (merged.location.x / 15 - this.txtViewLoc.x) * this.zoom - 40;
          merged.location.y = rect.top + (merged.location.y / 15 - this.txtViewLoc.y) * this.zoom - 40;
          merged.size.width = (merged.size.width / 15) * this.zoom + 80;
          merged.size.height = (merged.size.height / 15) * this.zoom + 80;
          this.spotlightService.placeSpotlight(merged);
        });
      });
    });
    TXTextControl.setEditMode(TXTextControl.EditMode.ReadOnly);
    let modalCollection: any = document.getElementsByClassName('find-and-replace-modal-dialog');
    this.modal = modalCollection.item(0) as HTMLElement;
    this.mainCanvas = document.getElementById('mainCanvas');
    this.fieldNavigatorSideBar = document.getElementById('fieldNavigatorSidebar');
    this.resetModalPosition();
    document.addEventListener('keydown', this.keyDownHandler);
  }

  async onFindClicked(forceReset: boolean = false) {
    if (!this.searchQuery) return;
    let result = await this.callFindNextInBody(forceReset);
    if (result === FindResult.NotFound) {
      result = await this.callFindNextInHeadersFooters();
    }
    if (result === FindResult.NotFound) {
      this.resetLastSearchResultLocations();
      this.textControlService.focus();
      TXTextControl.setActivationState(TXTextControl.ActivationState.Activated);
      this.showNotFoundDialog(forceReset == true);
    }
  }

  async callFindNextInBody(forceReset: boolean = false) {
    this.searchScope = SearchScope.Body;
    return new Promise((resolve) => {
      this.textControlService
        .findNextInBody(this.searchQuery, forceReset ? 0 : -1, this.findOptionsBitMask, this.txDocsSearchScope)
        .then((result) => {
          this.lastSearchResultBodyLocation = result;
          TXTextControl.setActivationState(TXTextControl.ActivationState.Activated);
          resolve(result);
        });
    });
  }

  async callFindNextInHeadersFooters() {
    this.searchScope = SearchScope.HeadersFooters;
    return new Promise((resolve) => {
      this.textControlService.findNextInHeaderOrFooter(this.searchQuery, -1, this.findOptionsBitMask).then((result) => {
        this.lastSearchResultHeaderFooterLocation = result;
        this.searchSelectionInvalid = this.textControlService.isFieldInRange(
          this.lastSearchResultBodyLocation,
          this.searchQuery.length
        );
        resolve(result);
      });
    });
  }

  async onReplaceClicked() {
    if (!this.searchQuery) {
      return;
    }
    try {
      var result: FormField | undefined = undefined;

      let selectedOptions = this.helperService.deconvertBitmask(this.findOptionsBitMask, FindOptions);
      await this.textControlService.callAsPromise(TXTextControl.beginUndoAction, 'Find And Replace');

      if (await this.findAndDeleteFieldsInSelection()) {
      }

      result = await this.textControlService.getUpdatedTokenWithReplacedText(
        this.searchQuery,
        this.replaceQuery,
        this.searchScope === SearchScope.Body
          ? this.lastSearchResultBodyLocation
          : this.lastSearchResultHeaderFooterLocation,
        selectedOptions.includes(FindOptions.MatchCase) ? true : false
      );

      if (result) {
        this.textControlService.replaceText(result);
      } else {
        await this.textControlService.replaceHighlightedText(this.replaceQuery);
      }
      this.textControlService.refreshFormFieldsArray([]);
    } catch (error) {
      console.error('Error replacing text', error);
    } finally {
      await this.textControlService.callAsPromiseNoParams(TXTextControl.endUndoAction);
      this.onFindClicked();
    }
  }

  async findAndDeleteFieldsInSelection(): Promise<boolean> {
    const txtext = new TextControlContext();
    const start = await txtext.selection.getStart();
    const length = await txtext.selection.getLength();

    const textSelection = await txtext.selection.getText();
    const textStart = (await txtext.selection.getStart()) - 1;
    const fieldsInSelection = this.textControlService.getFieldsInRange(textStart, textSelection.length);
    console.log(fieldsInSelection);
    if (fieldsInSelection.length == 0) {
      return false;
    }
    if (fieldsInSelection.length == 1) {
      const firstField = fieldsInSelection[0];
      if (firstField.positioning && firstField.positioning.start && firstField.positioning.length) {
        const fieldStart = firstField.positioning.start;
        const fieldEnd = fieldStart + firstField.positioning.length;
        const foundLocation = this.lastSearchResultBodyLocation;
        if (fieldStart <= foundLocation && foundLocation + this.searchQuery.length <= fieldEnd) {
          return false;
        }
      }
    }
    try {
      for (const field of fieldsInSelection) {
        const ff = await this.textControlService.getFieldById(field.details.id);
        await this.textControlService.deleteFormFieldAsync(new ffPromise(ff));
      }
    } catch {
      console.log('error deleting form field');
    }

    await txtext.selection.setStart(start);
    await txtext.selection.setLength(length);
    return true;
  }

  async onReplaceAllClicked() {
    if (!this.searchQuery) return;
    let selectedOptions = this.helperService.deconvertBitmask(this.findOptionsBitMask, FindOptions);
    await this.textControlService.replaceAllText(
      this.searchQuery,
      this.replaceQuery,
      this.txDocsSearchScope,
      this.findOptionsBitMask
    );
  }

  public onClose(): void {
    this.activeModal.close(true);
  }

  onFindWhatChanged(event: any) {
    this.resetLastSearchResultLocations();
    this.searchQuery = event.target.value;
  }

  onReplaceWhatChanged(event: any) {
    this.replaceQuery = event.target.value;
  }

  onMatchCaseChanged(event: any) {
    if (event.target.checked) {
      this.findOptionsBitMask += FindOptions.MatchCase;
    } else {
      this.findOptionsBitMask -= FindOptions.MatchCase;
    }
  }

  onMatchWholeWordChanged(event: any) {
    if (event.target.checked) {
      this.findOptionsBitMask += FindOptions.MatchWholeWord;
    } else {
      this.findOptionsBitMask -= FindOptions.MatchWholeWord;
    }
  }

  onSearchUpChanged(event: any) {
    if (this.searchUp == 'up') {
      this.findOptionsBitMask += FindOptions.SearchUp;
    } else {
      this.findOptionsBitMask -= FindOptions.SearchUp;
    }
  }

  onNoMessageBoxChanged(event: any) {
    if (event.target.checked) {
      this.findOptionsBitMask += FindOptions.NoMessageBox;
    } else {
      this.findOptionsBitMask -= FindOptions.NoMessageBox;
    }
  }

  async showNotFoundDialog(alreadyRestarted: boolean = false) {
    //this needs a ui refresh before showing
    setTimeout(() => {
      if (alreadyRestarted == true) {
        alert('Results not found for search: ' + this.searchQuery);
      } else {
        alert(
          'Could not find anymore results for search: ' + this.searchQuery + '\n\nStarting from top of the document'
        );
        this.onFindClicked(true);
      }
    }, 500);
  }

  resetLastSearchResultLocations() {
    this.lastSearchResultBodyLocation = 0;
    this.lastSearchResultHeaderFooterLocation = 0;
  }

  mergeRectangles(rect1: Rectangle, rect2: Rectangle): Rectangle {
    // Calculate the farthest left (smallest x) and top (smallest y)
    const left = Math.min(rect1.location.x, rect2.location.x);
    const top = Math.min(rect1.location.y, rect2.location.y);

    // Calculate the farthest right (largest x + width) and bottom (largest y + height)
    const right = Math.max(rect1.location.x + rect1.size.width, rect2.location.x + rect2.size.width);

    const bottom = Math.max(rect1.location.y + rect1.size.height, rect2.location.y + rect2.size.height);

    // The width and height of the resulting rectangle
    const width = right - left;
    const height = bottom - top;

    return {
      location: { x: left, y: top },
      size: { width, height },
    };
  }

  private resetModalPosition(skipboundCheck = false) {
    if (!this.modal || !this.fieldNavigatorSideBar) {
      let modalCollection: any = document.getElementsByClassName('find-and-replace-modal-dialog');
      this.modal = modalCollection.item(0) as HTMLElement;
      this.mainCanvas = document.getElementById('mainCanvas');
      this.fieldNavigatorSideBar = document.getElementById('fieldNavigatorSidebar');

      setTimeout(() => {
        this.resetModalPosition(skipboundCheck);
      }, 300);
      return;
    }

    const fieldNavigatorSideBarRect = this.fieldNavigatorSideBar.getBoundingClientRect();

    if (this.mainCanvas) {
      if (fieldNavigatorSideBarRect.width > 0) {
        this.modal.style.zIndex = '50';
        this.modal.style.top = `${fieldNavigatorSideBarRect.top}px`;
        this.modal.style.right = '0px';
        this.modal.style.width = `${fieldNavigatorSideBarRect.width}px`;
      }
    }
  }

  onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      this.onFindClicked();
    }
  }
}
